(() => {
  document.documentElement.classList.remove('no-js');

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (number == null || Number.isNaN(Number(number))) return '0';
    const fixed = (Number(number) / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts.join(decimal);
  }

  function money(cents, format) {
    const moneyFormat = format || window.AurisseTheme?.moneyFormat || '${{amount}}';
    const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return `$${formatWithDelimiters(cents)}`;
    let value;
    switch (placeholder[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }
    return moneyFormat.replace(placeholder[0], value);
  }

  function toast(message) {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    window.clearTimeout(el._aurisseTimer);
    el._aurisseTimer = window.setTimeout(() => el.classList.remove('is-visible'), 2800);
  }

  let lockedScrollY = 0;

  function lockPage(locked) {
    const html = document.documentElement;
    const body = document.body;
    if (locked) {
      if (html.classList.contains('aurisse-scroll-locked')) return;
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      html.classList.add('aurisse-scroll-locked');
      body.classList.add('aurisse-scroll-locked');
      body.dataset.aurisseScrollY = String(lockedScrollY);
      body.style.position = 'fixed';
      body.style.top = `-${lockedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      return;
    }

    const restoreY = Number(body.dataset.aurisseScrollY || lockedScrollY || 0);
    html.classList.remove('aurisse-scroll-locked');
    body.classList.remove('aurisse-scroll-locked');
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    delete body.dataset.aurisseScrollY;
    window.scrollTo(0, restoreY);
  }

  function openLayer(target) {
    if (!target) return;
    target.classList.add('is-open');
    target.setAttribute('aria-hidden', 'false');
    lockPage(true);
    window.setTimeout(() => qs('input, button, a', target)?.focus(), 30);
  }

  function closeLayer(target) {
    if (!target) return;
    target.classList.remove('is-open');
    target.setAttribute('aria-hidden', 'true');
    if (!qs('.is-open')) lockPage(false);
  }

  function initToggles(root = document) {
    qsa('[data-open-target]', root).forEach((button) => {
      if (button.dataset.aurisseBound) return;
      button.dataset.aurisseBound = 'true';
      button.addEventListener('click', () => openLayer(qs(button.dataset.openTarget)));
    });

    qsa('[data-close-target]', root).forEach((button) => {
      if (button.dataset.aurisseBound) return;
      button.dataset.aurisseBound = 'true';
      button.addEventListener('click', () => {
        const explicit = button.dataset.closeTarget ? qs(button.dataset.closeTarget) : null;
        closeLayer(explicit || button.closest('.is-open'));
      });
    });

    if (!document.documentElement.dataset.aurisseEscapeBound) {
      document.documentElement.dataset.aurisseEscapeBound = 'true';
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        qsa('.is-open').forEach(closeLayer);
      });
    }
  }

  function initAnnouncement(root = document) {
    qsa('[data-announcement]', root).forEach((slider) => {
      if (slider.dataset.aurisseBound) return;
      slider.dataset.aurisseBound = 'true';
      const slides = qsa('[data-announcement-slide]', slider);
      if (slides.length < 2) return;
      let index = 0;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      };
      const start = () => {
        window.clearInterval(timer);
        if (slider.dataset.autoplay === 'true') {
          timer = window.setInterval(() => show(index + 1), Number(slider.dataset.speed || 5000));
        }
      };
      qs('[data-announcement-prev]', slider)?.addEventListener('click', () => { show(index - 1); start(); });
      qs('[data-announcement-next]', slider)?.addEventListener('click', () => { show(index + 1); start(); });
      slider.addEventListener('mouseenter', () => window.clearInterval(timer));
      slider.addEventListener('mouseleave', start);
      start();
    });
  }

  function initHero(root = document) {
    qsa('[data-hero-slider]', root).forEach((slider) => {
      if (slider.dataset.aurisseBound) return;
      slider.dataset.aurisseBound = 'true';
      const slides = qsa('[data-hero-slide]', slider);
      const dots = qsa('[data-hero-dot]', slider);
      if (!slides.length) return;
      let index = 0;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      const start = () => {
        window.clearInterval(timer);
        if (slider.dataset.autoplay === 'true' && slides.length > 1) {
          timer = window.setInterval(() => show(index + 1), Number(slider.dataset.speed || 6000));
        }
      };
      qs('[data-hero-prev]', slider)?.addEventListener('click', () => { show(index - 1); start(); });
      qs('[data-hero-next]', slider)?.addEventListener('click', () => { show(index + 1); start(); });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      slider.addEventListener('mouseenter', () => window.clearInterval(timer));
      slider.addEventListener('mouseleave', start);
      start();
    });
  }

  function initTracks(root = document) {
    qsa('[data-slider]', root).forEach((slider) => {
      if (slider.dataset.aurisseBound) return;
      slider.dataset.aurisseBound = 'true';
      const track = qs('[data-slider-track]', slider);
      if (!track) return;
      const controlsScope = slider.closest('[data-slider-section]') || slider.parentElement;
      const previous = qs('[data-slider-prev]', controlsScope);
      const next = qs('[data-slider-next]', controlsScope);
      let timer;
      const amount = () => Math.max(Math.min(track.clientWidth * 0.86, 920), 280);
      const move = (direction) => track.scrollBy({ left: direction * amount(), behavior: 'smooth' });
      const start = () => {
        window.clearInterval(timer);
        if (slider.dataset.autoplay !== 'true') return;
        timer = window.setInterval(() => {
          const atEnd = Math.ceil(track.scrollLeft + track.clientWidth) >= track.scrollWidth - 6;
          track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + amount(), behavior: 'smooth' });
        }, Number(slider.dataset.speed || 4500));
      };
      previous?.addEventListener('click', () => { move(-1); start(); });
      next?.addEventListener('click', () => { move(1); start(); });
      slider.addEventListener('mouseenter', () => window.clearInterval(timer));
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', () => window.clearInterval(timer));
      slider.addEventListener('focusout', start);
      start();
    });
  }

  async function getCart() {
    const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart is temporarily unavailable.');
    return response.json();
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch(`${rootPath}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity) || 0) })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your cart.');
    return data;
  }

  function renderCart(cart) {
    qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    qsa('[data-cart-total-header]').forEach((el) => { el.textContent = money(cart.total_price); });
    const body = qs('[data-cart-drawer-body]');
    const total = qs('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price);
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML = '<div class="aurisse-empty"><p>Your cart is empty.</p><a class="aurisse-button" href="/collections/all">Continue shopping</a></div>';
    } else {
      body.innerHTML = cart.items.map((item, index) => {
        const image = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=220` : '';
        const title = String(item.product_title || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="aurisse-drawer-item__variant">${item.variant_title}</p>` : '';
        return `
          <div class="aurisse-drawer-item" data-cart-drawer-line="${index + 1}">
            <a class="aurisse-drawer-item__media" href="${item.url}">${image ? `<img src="${image}" alt="${title}" width="96" height="96">` : ''}</a>
            <div class="aurisse-drawer-item__content">
              <h3><a href="${item.url}">${item.product_title}</a></h3>
              ${variant}
              <div class="aurisse-drawer-quantity" aria-label="Quantity for ${title}">
                <button type="button" data-cart-drawer-change="-1" data-line="${index + 1}" aria-label="Decrease quantity">−</button>
                <span aria-live="polite">${item.quantity}</span>
                <button type="button" data-cart-drawer-change="1" data-line="${index + 1}" aria-label="Increase quantity">+</button>
              </div>
              <p class="aurisse-drawer-item__price"><strong>${money(item.final_line_price)}</strong>${item.original_line_price > item.final_line_price ? ` <s>${money(item.original_line_price)}</s>` : ''}</p>
            </div>
            <button class="aurisse-drawer-remove" type="button" data-cart-remove="${index + 1}" aria-label="Remove ${title}">×</button>
          </div>`;
      }).join('');
    }

    qsa('[data-cart-drawer-change]', body).forEach((button) => {
      button.addEventListener('click', async () => {
        const line = Number(button.dataset.line);
        const current = cart.items[line - 1]?.quantity || 0;
        button.disabled = true;
        try {
          renderCart(await changeCartLine(line, current + Number(button.dataset.cartDrawerChange || 0)));
        } catch (error) {
          toast(error.message);
          button.disabled = false;
        }
      });
    });

    qsa('[data-cart-remove]', body).forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          renderCart(await changeCartLine(Number(button.dataset.cartRemove), 0));
        } catch (error) {
          toast(error.message);
          button.disabled = false;
        }
      });
    });

    const footer = qs('[data-free-shipping-threshold]');
    const threshold = Number(footer?.dataset.freeShippingThreshold || 0);
    const progress = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100;
    qs('[data-shipping-progress]')?.style.setProperty('--progress', `${progress}%`);
    const shippingMessage = qs('[data-shipping-message]');
    if (shippingMessage && threshold > 0) {
      shippingMessage.textContent = cart.total_price >= threshold
        ? 'You unlocked free U.S. shipping.'
        : `${money(threshold - cart.total_price)} away from free U.S. shipping.`;
    }
  }

  async function openCart() {
    try { renderCart(await getCart()); } catch (error) { toast(error.message); }
    openLayer(qs('#AurisseCartDrawer'));
  }

  function initCart(root = document) {
    qsa('[data-cart-open]', root).forEach((button) => {
      if (button.dataset.aurisseBound) return;
      button.dataset.aurisseBound = 'true';
      button.addEventListener('click', (event) => { event.preventDefault(); openCart(); });
    });

    qsa('form[action*="/cart/add"]', root).forEach((form) => {
      if (form.dataset.aurisseBound) return;
      form.dataset.aurisseBound = 'true';
      form.addEventListener('submit', async (event) => {
        if (form.dataset.ajax === 'false') return;
        if (event.submitter && (event.submitter.name === 'checkout' || event.submitter.closest('.shopify-payment-button'))) return;
        event.preventDefault();
        const button = event.submitter || qs('[type="submit"]', form);
        const original = button?.textContent;
        if (button) { button.disabled = true; button.textContent = 'Adding…'; }
        try {
          const response = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Unable to add this item.');
          toast('Added to cart');
          await openCart();
        } catch (error) {
          toast(error.message);
        } finally {
          if (button) { button.disabled = false; button.textContent = original; }
        }
      });
    });
  }

  function initCartPage(root = document) {
    qsa('[data-cart-page]', root).forEach((cartPage) => {
      if (cartPage.dataset.aurisseBound) return;
      cartPage.dataset.aurisseBound = 'true';
      let busy = false;

      const updatePage = async (row, nextQuantity) => {
        if (busy || !row) return;
        busy = true;
        const controls = qsa('button, input', row);
        controls.forEach((control) => { control.disabled = true; });
        try {
          const line = Number(row.dataset.cartLine);
          const cart = await changeCartLine(line, nextQuantity);
          if (nextQuantity <= 0 || !cart.items[line - 1]) {
            window.location.reload();
            return;
          }
          const item = cart.items[line - 1];
          const input = qs('[data-cart-page-quantity]', row);
          if (input) input.value = item.quantity;
          const linePrice = qs('[data-cart-page-line-price]', row);
          if (linePrice) linePrice.textContent = money(item.final_line_price);
          const originalLine = qs('[data-cart-page-original-line]', row);
          if (originalLine) {
            originalLine.textContent = item.original_line_price > item.final_line_price ? money(item.original_line_price) : '';
            originalLine.hidden = !(item.original_line_price > item.final_line_price);
          }
          const unitPrice = qs('[data-cart-page-unit-price]', row);
          if (unitPrice) unitPrice.textContent = `${money(item.final_price)} each · Qty ${item.quantity}`;
          qsa('[data-cart-page-subtotal]').forEach((element) => { element.textContent = money(cart.total_price); });
          qsa('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; });
          qsa('[data-cart-total-header]').forEach((element) => { element.textContent = money(cart.total_price); });
        } catch (error) {
          toast(error.message);
        } finally {
          busy = false;
          controls.forEach((control) => { control.disabled = false; });
        }
      };

      qsa('[data-cart-page-action]', cartPage).forEach((button) => {
        button.addEventListener('click', () => {
          const row = button.closest('[data-cart-line]');
          const input = qs('[data-cart-page-quantity]', row);
          if (!input) return;
          const minimum = Number(input.min || 0);
          const next = Math.max(minimum, Number(input.value || 0) + Number(button.dataset.cartPageAction || 0));
          input.value = next;
          updatePage(row, next);
        });
      });

      qsa('[data-cart-page-quantity]', cartPage).forEach((input) => {
        input.addEventListener('change', () => {
          const row = input.closest('[data-cart-line]');
          updatePage(row, Math.max(Number(input.min || 0), Number(input.value || 0)));
        });
      });
    });
  }

  function initQuantity(root = document) {
    qsa('[data-quantity]', root).forEach((wrap) => {
      if (wrap.dataset.aurisseBound) return;
      wrap.dataset.aurisseBound = 'true';
      const input = qs('input', wrap);
      if (!input) return;
      qsa('button', wrap).forEach((button) => button.addEventListener('click', () => {
        const step = button.dataset.quantityAction === 'increase' ? 1 : -1;
        const minimum = Number(input.min || 1);
        const maximum = Number(input.max || 999);
        input.value = Math.max(minimum, Math.min(maximum, Number(input.value || 1) + step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
  }

  function initProduct(root = document) {
    qsa('[data-product-root]', root).forEach((productRoot) => {
      if (productRoot.dataset.aurisseBound) return;
      productRoot.dataset.aurisseBound = 'true';
      let variants = [];
      try { variants = JSON.parse(qs('[data-product-variants]', productRoot)?.textContent || '[]'); } catch (_) {}
      const idInput = qs('[name="id"]', productRoot);
      const price = qs('[data-product-price]', productRoot);
      const compare = qs('[data-product-compare]', productRoot);
      const add = qs('[data-product-add]', productRoot);
      const stock = qs('[data-product-stock]', productRoot);
      const stickyPrice = qs('[data-sticky-price]');
      const stickyAdd = qs('[data-sticky-add]');
      const bundleSelector = qs('[data-bundle-selector]', productRoot);
      const quantityInput = qs('.aurisse-product-form [name="quantity"]', productRoot);
      const bundleProperty = qs('[data-bundle-property]', productRoot);

      const updateBundleDisplays = (unitPrice) => {
        qsa('[data-bundle-total]', productRoot).forEach((element) => {
          const multiplier = Number(element.dataset.bundleMultiplier || 1);
          element.textContent = money(Number(unitPrice || 0) * multiplier);
        });
      };

      const applySelectedBundle = () => {
        if (!bundleSelector) return;
        const selected = qs('input[type="radio"]:checked', bundleSelector);
        if (!selected) return;
        qsa('.aurisse-volume-deal', bundleSelector).forEach((card) => {
          card.classList.toggle('is-selected', card.contains(selected));
        });
        if (quantityInput) {
          quantityInput.value = Math.max(1, Number(selected.dataset.bundleQuantity || selected.value || 1));
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (bundleProperty) bundleProperty.value = selected.dataset.bundleLabel || '';
      };

      const selectVariant = () => {
        const optionGroups = qsa('[data-option-index]', productRoot);
        const chosen = optionGroups.map((group) => qs('input:checked, select', group)?.value);
        const variant = optionGroups.length
          ? variants.find((candidate) => candidate.options.every((option, index) => option === chosen[index]))
          : variants.find((candidate) => String(candidate.id) === String(idInput?.value)) || variants[0];
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        if (price) price.textContent = money(variant.price);
        if (compare) {
          const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
          compare.textContent = hasCompare ? money(variant.compare_at_price) : '';
          compare.hidden = !hasCompare;
        }
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? (add.dataset.availableLabel || 'Add to cart') : 'Sold out';
        }
        if (stock) {
          stock.textContent = variant.available ? 'In stock and ready to ship' : 'Currently sold out';
          stock.classList.toggle('is-sold-out', !variant.available);
        }
        if (stickyPrice) stickyPrice.textContent = money(variant.price);
        updateBundleDisplays(variant.price);
        if (stickyAdd) {
          stickyAdd.disabled = !variant.available;
          stickyAdd.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      qsa('[data-option-index] input, [data-option-index] select', productRoot).forEach((input) => input.addEventListener('change', selectVariant));
      qsa('[data-bundle-selector] input[type="radio"]', productRoot).forEach((input) => input.addEventListener('change', applySelectedBundle));
      qsa('[data-gallery-thumb]', productRoot).forEach((button) => button.addEventListener('click', () => {
        qsa('[data-gallery-thumb]', productRoot).forEach((thumb) => thumb.classList.remove('is-active'));
        button.classList.add('is-active');
        const mediaId = button.dataset.mediaId;
        qsa('[data-gallery-media]', productRoot).forEach((media) => { media.hidden = media.dataset.mediaId !== mediaId; });
      }));
      selectVariant();
      applySelectedBundle();
    });
  }

  function initCollection(root = document) {
    qsa('[data-sort-by]', root).forEach((select) => {
      if (select.dataset.aurisseBound) return;
      select.dataset.aurisseBound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    });
    qsa('[data-filter-open]', root).forEach((button) => {
      if (button.dataset.aurisseBound) return;
      button.dataset.aurisseBound = 'true';
      button.addEventListener('click', () => qs('[data-collection-sidebar]')?.classList.add('is-open'));
    });
    qsa('[data-filter-close]', root).forEach((button) => {
      if (button.dataset.aurisseBound) return;
      button.dataset.aurisseBound = 'true';
      button.addEventListener('click', () => qs('[data-collection-sidebar]')?.classList.remove('is-open'));
    });
  }

  function initTracking(root = document) {
    qsa('[data-tracking-form]', root).forEach((form) => {
      if (form.dataset.aurisseBound) return;
      form.dataset.aurisseBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const order = qs('[name="order"]', form)?.value.trim();
        const email = qs('[name="email"]', form)?.value.trim();
        const message = qs('[data-tracking-message]', form.parentElement);
        if (!order || !email) {
          if (message) {
            message.textContent = 'Please enter both your order ID and billing email.';
            message.className = 'aurisse-form-message is-visible is-error';
          }
          return;
        }
        const template = form.dataset.lookupUrl;
        if (template) {
          window.location.href = template.replace('{order}', encodeURIComponent(order)).replace('{email}', encodeURIComponent(email));
        } else if (message) {
          message.textContent = 'Connect a tracking app block or add its lookup URL in this section. Customers can also use the secure account button below.';
          message.className = 'aurisse-form-message is-visible is-error';
        }
      });
    });
  }

  function initReveal(root = document) {
    if (!('IntersectionObserver' in window)) {
      qsa('[data-reveal]', root).forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
    qsa('[data-reveal]', root).forEach((el) => {
      if (el.dataset.aurisseRevealBound) return;
      el.dataset.aurisseRevealBound = 'true';
      observer.observe(el);
    });
  }

  function init(root = document) {
    initToggles(root);
    initAnnouncement(root);
    initHero(root);
    initTracks(root);
    initCart(root);
    initCartPage(root);
    initQuantity(root);
    initProduct(root);
    initCollection(root);
    initTracking(root);
    initReveal(root);
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
